import { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaChevronRight,
  FaCalendarAlt,
  FaChevronDown
} from "react-icons/fa";

import API from "../api/api";
import AdminLayout from "../layouts/AdminLayout";
import "./Bookings.css";

export default function Bookings() {

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [page, setPage] = useState(1);
const [totalBookings, setTotalBookings] = useState(0);
const [totalPages, setTotalPages] = useState(1);

  /* =====================================================
     LOAD BOOKINGS
  ===================================================== */

useEffect(() => {

  const timer = setTimeout(() => {
    fetchData();
  }, 350);

  return () => clearTimeout(timer);

}, [page, statusFilter, search]);


  const fetchData = async () => {

    try {

      setLoading(true);

   const res = await API.get("/bookings", {
  params: {
    page,
    limit: 100,
    status: statusFilter,
    search: search.trim()
  }
});

      setBookings(
  Array.isArray(res.data.bookings)
    ? res.data.bookings
    : []
);

setTotalBookings(
  Number(res.data.total || 0)
);

setTotalPages(
  Number(res.data.totalPages || 1)
);

  setStatusCounts(
      res.data.statusCounts || {
        all: 0,
        confirmed: 0,
        cancelled: 0,
        unpaid: 0,
        paid: 0,
        invoiced: 0
      }
    );

    } catch (err) {

      console.error("BOOKINGS ERROR:", err);

      setBookings([]);

    } finally {

      setLoading(false);

    }

  };


  /* ================= PAGINATION ================= */

const changePage = (newPage) => {

  if (
    newPage < 1 ||
    newPage > totalPages
  ) {
    return;
  }

  setPage(newPage);

};

  /* =====================================================
     HELPERS
  ===================================================== */

  const getGuestName = (booking) => {

    return (
      booking.guest_name ||
      `${booking.first_name || ""} ${booking.last_name || ""}`.trim() ||
      "Unknown guest"
    );

  };


  const getInitials = (booking) => {

    const name = getGuestName(booking);

    const parts = name
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!parts.length) return "?";

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return (
      parts[0][0] +
      parts[parts.length - 1][0]
    ).toUpperCase();

  };


  const formatTourDate = (dateString) => {

    if (!dateString) {
      return {
        date: "-",
        time: "-"
      };
    }

    const date = new Date(dateString);

    return {
      date: date.toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric"
        }
      ),

      time: date.toLocaleTimeString(
        "en-GB",
        {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false
        }
      )
    };

  };


  const formatPrice = (value) => {

    const amount = Number(value || 0);

    return `€${amount.toLocaleString(
      "en-GB",
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }
    )}`;

  };


const getReference = (booking) => {

  return String(
    booking.bokun_booking_id ||
    `#${booking.id}`
  );

};


  const getChannel = () => {
    /*
      Current bookings come through the application's
      local booking/import pipeline.
      We can replace this with a real Bokun channel
      once that field is stored in the database.
    */

    return "Bokun";
  };


  const isDateInRange = (booking) => {

    if (dateFilter === "all") {
      return true;
    }

    if (!booking.start_datetime) {
      return false;
    }

    const tourDate =
      new Date(booking.start_datetime);

    const today = new Date();

    today.setHours(0, 0, 0, 0);

    if (dateFilter === "today") {

      return (
        tourDate.toDateString() ===
        today.toDateString()
      );

    }

    if (dateFilter === "tomorrow") {

      const tomorrow =
        new Date(today);

      tomorrow.setDate(
        tomorrow.getDate() + 1
      );

      return (
        tourDate.toDateString() ===
        tomorrow.toDateString()
      );

    }

    if (dateFilter === "next_7_days") {

      const end =
        new Date(today);

      end.setDate(
        end.getDate() + 7
      );

      return (
        tourDate >= today &&
        tourDate <= end
      );

    }

    if (dateFilter === "next_30_days") {

      const end =
        new Date(today);

      end.setDate(
        end.getDate() + 30
      );

      return (
        tourDate >= today &&
        tourDate <= end
      );

    }

    return true;

  };


  /* =====================================================
     COUNTS
  ===================================================== */

  const [statusCounts, setStatusCounts] = useState({
  all: 0,
  confirmed: 0,
  cancelled: 0,
  unpaid: 0,
  paid: 0,
  invoiced: 0
});

/* =====================================================
   FILTER
===================================================== */

const filtered = bookings.filter(booking => {

  const channel =
    getChannel(booking);

  const matchesChannel =
    channelFilter === "all" ||
    channel === channelFilter;

  const matchesDate =
    isDateInRange(booking);

  return (
    matchesChannel &&
    matchesDate
  );

});

  /* =====================================================
     RENDER
  ===================================================== */

  return (

    <AdminLayout>

      <div className="bookings-page">


        {/* =================================================
           HEADER
        ================================================= */}

        <div className="bookings-header">

          <div>

            <div className="breadcrumbs">
              
              <span>›</span>
              Bookings
            </div>

            <div className="bookings-title-row">

              <h1>
                Bookings
              </h1>

              <span className="booking-total">
                {totalBookings.toLocaleString()}
              </span>

            </div>

            <p className="bookings-subtitle">
              Manage all reservations and track their status.
            </p>

          </div>

        </div>


        {/* =================================================
           SEARCH + DROPDOWNS
        ================================================= */}

        <div className="booking-toolbar">

          <div className="booking-search">

            <FaSearch />

           <input
  type="text"
  placeholder="Search by reference, guest name or tour..."
  value={search}
  onChange={(e) => {
    setSearch(e.target.value);
    setPage(1);
  }}
/>

          </div>


          <div className="toolbar-select">

            <FaCalendarAlt />

            <select
              value={dateFilter}
              onChange={(e) =>
                setDateFilter(e.target.value)
              }
            >

              <option value="all">
                All dates
              </option>

              <option value="today">
                Today
              </option>

              <option value="tomorrow">
                Tomorrow
              </option>

              <option value="next_7_days">
                Next 7 days
              </option>

              <option value="next_30_days">
                Next 30 days
              </option>

            </select>

            <FaChevronDown />

          </div>


          <div className="toolbar-select">

            <select
              value={channelFilter}
              onChange={(e) =>
                setChannelFilter(e.target.value)
              }
            >

              <option value="all">
                All channels
              </option>

              <option value="Bokun">
                Bokun
              </option>

            </select>

            <FaChevronDown />

          </div>

        </div>


        {/* =================================================
           STATUS TABS
        ================================================= */}

        <div className="status-tabs">

          <button
            className={
              statusFilter === "all"
                ? "status-tab active"
                : "status-tab"
            }
            onClick={() =>
              setStatusFilter("all")
            }
          >
            All
            <span>{statusCounts.all}</span>
          </button>


          <button
            className={
              statusFilter === "confirmed"
                ? "status-tab active"
                : "status-tab"
            }
            onClick={() =>
              setStatusFilter("confirmed")
            }
          >
            Confirmed
            <span>{statusCounts.confirmed}</span>
          </button>


          <button
            className={
              statusFilter === "cancelled"
                ? "status-tab active"
                : "status-tab"
            }
            onClick={() =>
              setStatusFilter("cancelled")
            }
          >
            Cancelled
            <span>{statusCounts.cancelled}</span>
          </button>


          <button
            className={
              statusFilter === "unpaid"
                ? "status-tab active"
                : "status-tab"
            }
            onClick={() =>
              setStatusFilter("unpaid")
            }
          >
            Unpaid
            <span>{statusCounts.unpaid}</span>
          </button>


          <button
            className={
              statusFilter === "paid"
                ? "status-tab active"
                : "status-tab"
            }
            onClick={() =>
              setStatusFilter("paid")
            }
          >
            Paid
            <span>{statusCounts.paid}</span>
          </button>


          <button
            className={
              statusFilter === "invoiced"
                ? "status-tab active"
                : "status-tab"
            }
            onClick={() =>
              setStatusFilter("invoiced")
            }
          >
            Invoiced
            <span>{statusCounts.invoiced}</span>
          </button>

        </div>


        {/* =================================================
           TABLE
        ================================================= */}

        <div className="bookings-table-card">

          {loading ? (

            <div className="bookings-empty">
              Loading bookings...
            </div>

            

          ) : filtered.length === 0 ? (

            <div className="bookings-empty">
              No bookings found
            </div>

          ) : (

            <div className="bookings-table-wrapper">

              <table className="bookings-table">

                <thead>

                  <tr>

                    <th>
                      Reference
                    </th>

                    <th>
                      Guest
                    </th>

                    <th>
                      Tour
                    </th>

                    <th>
                      Date & Time
                    </th>

                    <th>
                      Channel
                    </th>

                    <th>
                      Amount
                    </th>

                    <th>
                      Status
                    </th>

                    <th></th>

                  </tr>

                </thead>


                <tbody>

                  {filtered.map(booking => {

                    const tourDate =
                      formatTourDate(
                        booking.start_datetime
                      );

                    return (

                      <tr key={booking.id}>


                        {/* REFERENCE */}

                        <td>

                          <span className="booking-reference">
                            {getReference(booking)}
                          </span>

                        </td>


                        {/* GUEST */}

                        <td>

                          <div className="guest-cell">

                            <div className="guest-avatar">
                              {getInitials(booking)}
                            </div>

                            <div>

                              <div className="guest-name">
                                {getGuestName(booking)}
                              </div>

                              {booking.email && (
                                <div className="guest-email">
                                  {booking.email}
                                </div>
                              )}

                            </div>

                          </div>

                        </td>


                        {/* TOUR */}

                        <td>

                          <div className="tour-cell">

                            <div className="tour-name">
                              {booking.tour_name || "-"}
                            </div>

                            <div className="guest-count">
                              {Number(
                                booking.guest_count || 0
                              )}{" "}
                              {Number(
                                booking.guest_count || 0
                              ) === 1
                                ? "guest"
                                : "guests"}
                            </div>

                          </div>

                        </td>


                        {/* DATE */}

                        <td>

                          <div className="tour-date">

                            <strong>
                              {tourDate.date}
                            </strong>

                            <span>
                              {tourDate.time}
                            </span>

                          </div>

                        </td>


                        {/* CHANNEL */}

                        <td>

                          <span className="channel-text">
                            {getChannel(booking)}
                          </span>

                        </td>


                        {/* AMOUNT */}

                        <td>

                          <strong className="booking-amount">
                            {formatPrice(
                              booking.total_price
                            )}
                          </strong>

                        </td>


                        {/* STATUS */}

                        <td>

                          <span
                            className={`booking-status status-${booking.status}`}
                          >
                            <span className="status-dot" />

                            {booking.status || "unknown"}
                          </span>

                        </td>


                        {/* ACTION */}

                        <td>

                          <button
                            className="booking-action"
                            type="button"
                            aria-label="Open booking"
                          >
                            <FaChevronRight />
                          </button>

                        </td>

                      </tr>

                    );

                  })}

                </tbody>

              </table>

            </div>

          )}

        </div>
<div className="bookings-pagination">

  <div className="pagination-info">
    Showing{" "}
    <strong>
      {totalBookings === 0
        ? 0
        : ((page - 1) * 100) + 1}
    </strong>
    {" "}–{" "}
    <strong>
      {Math.min(
        page * 100,
        totalBookings
      )}
    </strong>
    {" "}of{" "}
    <strong>
      {totalBookings}
    </strong>
  </div>


  <div className="pagination-buttons">

    <button
      disabled={page === 1}
      onClick={() =>
        changePage(page - 1)
      }
    >
      Previous
    </button>


    <span className="pagination-current">
      {page}
    </span>


    <button
      disabled={page === totalPages}
      onClick={() =>
        changePage(page + 1)
      }
    >
      Next
    </button>

  </div>

</div>

      </div>

    </AdminLayout>

  );

}